import { useTranslation } from 'react-i18next';
import {
  ExTable,
  ExEmptyState,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
} from '@boomi/exosphere';
import { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import type { BoomiModel } from '@components/ModelsManagement/types/models.types';
import { providerLogo } from '@components/ModelsManagement';
import modelsApiService from '@services/agent-foundation/agent-foundation-models-api-service';
import noResultsImage from '@assets/boomi-ai/ui-kit/agentFoundation/No_result.svg';
import './BoomiModelsTable.scss';

function BoomiModelsTable() {
  const [t] = useTranslation('common');
  const [gridApi, setGridApi] = useState<any>(null);
  const [boomiModels, setBoomiModels] = useState<BoomiModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    modelsApiService.getBoomiModels()
      .then((models) => {
        setBoomiModels(models);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);
  const getModelText = (processingMode: any) => {
    if (processingMode === 'quick') return t('Fast');
    if (processingMode === 'standard') return t('Standard');
    return '';
  };
  const modelNameCellRenderer = (params: any) => {
    const container = document.createElement('div');
    const root = createRoot(container);
    const logo = providerLogo('Boomi');
    root.render(
      <div style={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: '500px',
        paddingTop: '4px',
      }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          backgroundColor: 'var(--application-bg-color)',
          borderRadius: '4px',
          flexShrink: 0,
        }}
        >
          <img src={logo.src} alt={logo.alt} style={{ width: '20px', height: '20px' }} />
        </div>
        <div style={{ marginLeft: '8px', overflow: 'hidden' }}>
          <ExTooltip
            tooltipDelay={0}
            position={TooltipPosition.TOP}
            alignment={TooltipAlignment.MIDDLE}
          >
            <div style={{
              display: 'flex',
            }}
            >
              {params.data.display_name}
            </div>
            <button
              type="button"
              slot="anchor"
              data-locator-id="model-list-model-name-cell"
              style={{
                display: 'block',
                background: 'none',
                border: 'none',
                fontSize: 'var(--exo-font-size-small)',
                paddingLeft: 0,
                fontWeight: 400,
                lineHeight: '15px',
                paddingBottom: '6px',
                maxWidth: '100%',
                fontFamily: 'inherit',
              }}
              data-testid="boomi-model-name"
            >
              {params.data.display_name}
            </button>
          </ExTooltip>
          <div
            style={{
              color: 'var(--exo-color-font-secondary)',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '15px',
            }}
            data-testid="boomi-model-processing-mode"
          >
            {getModelText(params.data.processing_mode)}
          </div>
        </div>
      </div>,
    );
    return container;
  };
  const valueRenderer = (value: string) => {
    const container = document.createElement('div');
    container.style.display = 'flex';
    const root = createRoot(container);
    root.render(
      <ExTooltip
        tooltipDelay={0}
        position={TooltipPosition.TOP}
        alignment={TooltipAlignment.MIDDLE}
      >
        <div style={{ display: 'flex' }}>
          {value || '-'}
        </div>
        <div
          slot="anchor"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
            fontSize: '14px',
            fontWeight: '400',
          }}
          data-testid="boomi-model-description"
        >
          {value || '-'}
        </div>
      </ExTooltip>,
    );
    return container;
  };
  const descriptionCellRenderer = (params: any) => {
    const value = params.data?.description?.length > 500 ? `${params.data.description?.substring(0, 500)}...` : params.data?.description;
    return valueRenderer(value);
  };
  const columnDefs = [
    {
      field: 'name',
      headerName: t('boomi_models_table_column_name'),
      minWidth: 200,
      cellRenderer: (params: any) => modelNameCellRenderer(params),
      resizable: true,
    },
    {
      field: 'description',
      headerName: t('models_table_column_description'),
      flex: 2,
      cellRenderer: (params: any) => descriptionCellRenderer(params),
      resizable: true,
    },
  ];

  const [gridOptions] = useState<any>({
    headerHeight: 36,
    defaultColDef: {
      resizable: true,
      sortable: false,
      cellStyle: { fontSize: '14px', fontFamily: 'Noto Sans' },
    },
    columnDefs,
    rowData: [],
    domLayout: 'normal',
    suppressPaginationPanel: true,
    rowHeight: 56,
    popupParent: document.querySelector('body'),
    suppressRowTransform: true,
    animateRows: true,
    suppressDragLeaveHidesColumns: true,
    suppressMovableColumns: true,
    onGridReady: (event: any) => {
      setGridApi(event.api);
    },
  });

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption('rowData', boomiModels);
    }
  }, [gridApi, boomiModels]);

  if (isLoading) {
    return (
      <div className="boomi-models-table__loader">
        <ExLoader
          size={LoaderSize.MEDIUM}
          variant={LoaderVariant.SPINNER}
          label={t('ai_loading')}
        />
      </div>
    );
  }

  if (boomiModels.length === 0) {
    return (
      <ExEmptyState
        label={t('glossary_panel_error_title')}
        text={t('data_load_error_description')}
        className="models-boomi-page-no-results"
      >
        <div slot="header" data-testid="models-no-results-header">
          <img
            alt={t('models_no_results_alt')}
            src={noResultsImage}
            data-testid="models-no-results-image"
          />
        </div>
      </ExEmptyState>
    );
  }

  return (
    <div className="boomi-models-table">
      <ExTable
        gridOptions={gridOptions}
        domLayoutNormal
        stickyHeader
        data-testid="boomi-models-table"
      />
    </div>
  );
}

BoomiModelsTable.defaultProps = {
};

export default BoomiModelsTable;
